import { SyntheticEvent } from 'react';
import classNames from 'classnames';
import { OverlayTriggerProps } from 'react-bootstrap/OverlayTrigger';

import ELEMENT from 'common/element';
import Link from 'components/common/Link/Link';
import ElementWithTooltip from 'components/common/ElementWithTooltip/ElementWithTooltip';

import './IconButton.scss';

export type IconButtonProps = {
  icon: JSX.Element;
  onClick?: (e: SyntheticEvent) => void;
  tooltip?: string;
  className?: string;
  containerClassName?: string;
  toolTipPlacement?: OverlayTriggerProps['placement'];
  showTooltip?: boolean;
  href?: string;
  onEnterTooltip?: () => void;
  onExitedTooltip?: () => void;
  disabled?: boolean;
  testId?: ELEMENT;
  trigger?: OverlayTriggerProps['trigger'];
  children?: JSX.Element;
};

type LinkButtonProps = {
  href?: string;
  testId?: ELEMENT;
  className?: string;
  children?: JSX.Element;
  icon: JSX.Element;
};

const LinkButton = ({ icon, href, testId, className, children }: LinkButtonProps) => (
  <Link className={className} href={href} testId={testId}>
    {icon}
    {children}
  </Link>
);

/**
 * Icon button with a tooltip.
 * @prop icon                     The icon element
 * @prop onClick                  The click callback on the button
 * @prop tooltip                  The tooltip message
 * @prop className                className for the icon button
 * @prop containerClassName       className used to target the container element
 * @prop toolTipPlacement         The placement of the tooltip
 * @prop showTooltip              If true, show the tooltip
 * @prop href                     If present, iconButton is an anchor element instead of a button
 * @prop onEnterTooltip           Callback fired before the Overlay transitions in
 * @prop onExitedTooltip          Callback fired after the Overlay finishes transitioning out
 * @prop testId                   Test id for testing
 * @prop trigger                  Specify which action or actions trigger Overlay visibility
 *
 * The odd default value for 'trigger' below is to prevent a React warning. See:
 *     https://github.com/react-bootstrap/react-bootstrap/issues/5027
 */

// TODO: could Button be extended to replace this?
const IconButton = ({
  icon,
  onClick,
  tooltip,
  className,
  containerClassName,
  toolTipPlacement = 'bottom',
  showTooltip = true,
  href,
  onEnterTooltip,
  onExitedTooltip,
  disabled,
  testId,
  trigger = ['hover', 'hover'],
  children,
}: IconButtonProps) => {
  const iconButton = (
    <div className={classNames('IconButton__OverlayWrapper', containerClassName)}>
      {href ? (
        <LinkButton icon={icon} className={classNames('IconButton', className)} href={href} testId={testId}>
          {children}
        </LinkButton>
      ) : (
        <button
          className={classNames('IconButton', className)}
          onClick={onClick}
          data-test-id={testId}
          disabled={disabled}
          type="button"
        >
          {icon}
          {children}
        </button>
      )}
    </div>
  );

  if (!showTooltip || !tooltip) {
    return iconButton;
  }

  return (
    <ElementWithTooltip
      placement={toolTipPlacement}
      onEnter={onEnterTooltip}
      onExited={onExitedTooltip}
      trigger={trigger}
      tooltip={tooltip}
    >
      {iconButton}
    </ElementWithTooltip>
  );
};

export default IconButton;
